import { useEffect, useRef } from 'react';
import { format, subDays } from 'date-fns';
import { expandActivityOccurrences } from '@coparent/shared/src/utils/activityUtils';
import { expandRecurringExpenseOccurrences } from '@coparent/shared/src/utils/recurringExpenseUtils';
import { createExpense, deleteDuplicateActivityExpenses } from '@coparent/shared/src/services/expenseService';
import { getChildIds } from '@coparent/shared/src/utils/childUtils';

const LOOKBACK_DAYS = 30;
const MAX_PER_SYNC = 10;

const useActivityExpenseSync = (familyId, activities, expenses, currentUser, recurringExpenses = []) => {
  const isSyncing = useRef(false);
  const createdKeys = useRef(new Set());
  const hasCleanedUp = useRef(false);

  // One-time cleanup of existing duplicates
  useEffect(() => {
    if (!familyId || hasCleanedUp.current) return;
    hasCleanedUp.current = true;

    deleteDuplicateActivityExpenses(familyId)
      .then(count => {
        if (count > 0) console.log(`Cleaned up ${count} duplicate activity expenses`);
      })
      .catch(err => console.error('Failed to clean up duplicate expenses:', err));
  }, [familyId]);

  // Sync activity-based expenses
  useEffect(() => {
    if (!familyId || !activities.length || !currentUser || isSyncing.current) return;

    const costedActivities = activities.filter(a => a.cost && a.cost > 0);
    if (!costedActivities.length) return;

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const yesterday = subDays(today, 1);
    const lookbackStart = subDays(today, LOOKBACK_DAYS);

    const existingKeys = new Set();
    expenses.forEach(e => {
      if (e.activityId && e.activityDate) {
        existingKeys.add(`${e.activityId}:${e.activityDate}`);
      }
    });

    const missing = [];
    costedActivities.forEach(activity => {
      const occurrences = expandActivityOccurrences(activity, lookbackStart, yesterday);
      occurrences.forEach(({ date: dateStr }) => {
        const key = `${activity.id}:${dateStr}`;
        if (!existingKeys.has(key) && !createdKeys.current.has(key)) {
          missing.push({ activity, dateStr });
        }
      });
    });

    if (!missing.length) return;

    const batch = missing.slice(0, MAX_PER_SYNC);
    isSyncing.current = true;

    batch.forEach(({ activity, dateStr }) => {
      createdKeys.current.add(`${activity.id}:${dateStr}`);
    });

    const createAll = async () => {
      for (const { activity, dateStr } of batch) {
        const key = `${activity.id}:${dateStr}`;
        try {
          await createExpense(familyId, {
            description: `${activity.name} — ${format(new Date(dateStr + 'T00:00:00'), 'MMM d')}`,
            amount: activity.cost,
            date: new Date(dateStr + 'T00:00:00'),
            category: 'activities',
            childIds: getChildIds(activity),
            paidBy: activity.paidBy || currentUser.uid,
            splitRatio: activity.costSplitRatio ?? 50,
            note: 'Auto-created from activity',
            activityId: activity.id,
            activityDate: dateStr,
            autoCreated: true,
            createdBy: currentUser.uid,
            receiptURL: null,
          });
        } catch (err) {
          console.error('Failed to auto-create expense:', err);
          createdKeys.current.delete(key);
        }
      }
      isSyncing.current = false;
    };

    createAll();
  }, [familyId, activities, expenses, currentUser]);

  // Sync recurring expenses (not tied to activities)
  useEffect(() => {
    if (!familyId || !recurringExpenses.length || !currentUser || isSyncing.current) return;

    const activeRecurring = recurringExpenses.filter(r => r.isActive);
    if (!activeRecurring.length) return;

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const yesterday = subDays(today, 1);
    const lookbackStart = subDays(today, LOOKBACK_DAYS);

    // Build set of existing recurring-expense keys
    const existingKeys = new Set();
    expenses.forEach(e => {
      if (e.recurringExpenseId && e.recurringExpenseDate) {
        existingKeys.add(`recurring:${e.recurringExpenseId}:${e.recurringExpenseDate}`);
      }
    });

    const missing = [];
    activeRecurring.forEach(re => {
      const occurrences = expandRecurringExpenseOccurrences(re, lookbackStart, yesterday);
      occurrences.forEach(dateStr => {
        const key = `recurring:${re.id}:${dateStr}`;
        if (!existingKeys.has(key) && !createdKeys.current.has(key)) {
          missing.push({ recurringExpense: re, dateStr });
        }
      });
    });

    if (!missing.length) return;

    const batch = missing.slice(0, MAX_PER_SYNC);
    isSyncing.current = true;

    batch.forEach(({ recurringExpense, dateStr }) => {
      createdKeys.current.add(`recurring:${recurringExpense.id}:${dateStr}`);
    });

    const createAll = async () => {
      for (const { recurringExpense: re, dateStr } of batch) {
        const key = `recurring:${re.id}:${dateStr}`;
        try {
          await createExpense(familyId, {
            description: `${re.description} — ${format(new Date(dateStr + 'T00:00:00'), 'MMM d')}`,
            amount: re.amount,
            date: new Date(dateStr + 'T00:00:00'),
            category: re.category || 'other',
            childIds: re.childIds || [],
            paidBy: re.paidBy,
            splitRatio: re.splitRatio ?? 50,
            note: 'Auto-created from recurring expense',
            recurringExpenseId: re.id,
            recurringExpenseDate: dateStr,
            autoCreated: true,
            createdBy: currentUser.uid,
            receiptURL: null,
          });
        } catch (err) {
          console.error('Failed to auto-create recurring expense:', err);
          createdKeys.current.delete(key);
        }
      }
      isSyncing.current = false;
    };

    createAll();
  }, [familyId, recurringExpenses, expenses, currentUser]);
};

export default useActivityExpenseSync;
